import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useStore } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";

import { BUILD_VERSION } from "src/config";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import { isLoggedInSelector } from "src/store/system/selector";
import { getBrowserId } from "src/utils/browserId";
import { isNewerVersion } from "src/utils/buildVersion";
import StatusesContainer from "src/pages/RootDialogs/IntegrationSyncerDialog/StatusesContainer";
import Status from "src/pages/RootDialogs/IntegrationSyncerDialog/Status";

const CheckIntervalMS = 5 * 60 * 1000;

function isSupportPath(pathname: string) {
  return pathname === "/support" || pathname.startsWith("/support/");
}

const useStyles = makeStyles({
  clickHere: {
    cursor: "pointer",
    textDecoration: "underline",
  },
});

function AppVersionBanner() {
  const classes = useStyles();
  const { pathname } = useLocation();
  const isSupportPage = isSupportPath(pathname);
  const [showBanner, setShowBanner] = useState(false);
  const store = useStore();
  const storeRef = useRef(store);
  storeRef.current = store;
  const recordEvent = useRecordEvent();
  const recordEventRef = useRef(recordEvent);
  recordEventRef.current = recordEvent;

  useEffect(() => {
    if (
      !BUILD_VERSION ||
      process.env.NODE_ENV !== "production" ||
      isSupportPage
    ) {
      setShowBanner(false);
      return;
    }

    let timeoutId: number | undefined;
    let cancelled = false;

    async function checkVersion() {
      if (!BUILD_VERSION || cancelled) return;

      try {
        const response = await fetch(`/version?t=${Date.now()}`, {
          cache: "no-store",
        });
        if (!response.ok || cancelled) return;

        const serverVersion = (await response.text()).trim();
        if (cancelled) return;

        if (isLoggedInSelector(storeRef.current.getState())) {
          recordEventRef.current({
            type: "version-check",
            clientVersion: BUILD_VERSION,
            serverVersion,
            browserId: getBrowserId(),
          });
        }

        if (!isNewerVersion(serverVersion, BUILD_VERSION)) {
          if (!cancelled) setShowBanner(false);
          return;
        }

        console.log(
          `New app version available: ${serverVersion} (current: ${BUILD_VERSION})`
        );
        if (!cancelled) setShowBanner(true);
      } catch {
        // Ignore network errors during background checks.
      } finally {
        if (!cancelled) {
          timeoutId = window.setTimeout(() => {
            void checkVersion();
          }, CheckIntervalMS);
        }
      }
    }

    void checkVersion();

    return () => {
      cancelled = true;
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, [isSupportPage]);

  const handleReload = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("t", String(Date.now()));
    window.location.assign(url.toString());
  }, []);

  if (!showBanner) return null;

  return (
    <StatusesContainer>
      <Status
        open
        severity="error"
        message={
          <div>
            <span className={classes.clickHere} onClick={handleReload}>
              Click here
            </span>{" "}
            to get the latest updates to My Reseller Genie. You&apos;re
            currently using an outdated version.
          </div>
        }
        onClose={() => setShowBanner(false)}
      />
    </StatusesContainer>
  );
}

export default AppVersionBanner;
